import React, {useState} from 'react';
import {Alert, Box, Checkbox, FormControlLabel, Radio, RadioGroup, Typography} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {useRecordContext, usePermissions, useTranslate} from 'react-admin';
import {t} from '../themes/statamicTokens';
import {axiosApiInstance} from '../AxiosUtils';
import hasPermission from '../auth/hasPermission';

/**
 * "Send test mail" panel. Standalone (no form context) — maintains its own
 * local state so it can live inside an Edit rail or a Show sidebar without
 * colliding with the record form.
 */
const MailTestPanel = () => {
    const translate = useTranslate();
    const record = useRecordContext();
    const {permissions} = usePermissions();

    const [email, setEmail] = useState('');
    const [newSponsor, setNewSponsor] = useState(true);
    const [donationMethod, setDonationMethod] = useState('iban');
    const [mailType, setMailType] = useState('registration');
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);

    const sendTestMail = () => {
        if (!email) {
            setResult({type: 'warning', text: translate('Email address is required')});
            return;
        }
        if (!record) return;

        setSending(true);
        setResult(null);

        const rolePath = permissions === 'admin' ? 'admin' : 'umbrellad';
        const postFix = hasPermission(permissions, ['mailsettings'], 'umbrella') ? `${record.charity_id}` : '';
        const formData = new FormData();
        formData.append('email', email);
        formData.append('new_sponsor', newSponsor ? 'true' : 'false');
        formData.append('donation_method', donationMethod);
        formData.append('code', record.language_code ?? '');

        axiosApiInstance.post(
            `${process.env.REACT_APP_API_URL}/api/v1/${rolePath}/sendTestMailV2/${mailType}/${postFix}`,
            formData,
            {headers: {'Content-Type': 'multipart/form-data'}},
        )
            .then(() => {
                setSending(false);
                setResult({type: 'success', text: translate('Test mail sent')});
            })
            .catch(() => {
                setSending(false);
                setResult({type: 'warning', text: translate('Could not send test mail')});
            });
    };

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                {translate('Send a sample of this template to verify layout and content.')}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.25,
                    py: 0.75,
                    minHeight: 36,
                    borderRadius: `${t.radius.sm}px`,
                    border: `1px solid ${t.color.border.default}`,
                    backgroundColor: t.color.surface,
                    '&:focus-within': {
                        borderColor: t.color.accent.primary,
                        boxShadow: `0 0 0 3px ${t.color.accent.primaryTint}`,
                    },
                }}
            >
                <Box
                    component="input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={translate('Email address')}
                    sx={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        fontSize: `${t.type.body.size}px`,
                        color: t.color.text.primary,
                        backgroundColor: 'transparent',
                    }}
                />
            </Box>

            <Box>
                <Typography sx={{fontSize: `${t.type.caption.size}px`, fontWeight: 500, color: t.color.text.secondary, mb: 0.5}}>
                    {translate('Mail type')}
                </Typography>
                <RadioGroup value={mailType} onChange={(e) => setMailType(e.target.value)}>
                    {[
                        {v: 'registration', label: translate('Registration mail')},
                        {v: 'update',       label: translate('Reminder for new posts')},
                        {v: 'new',          label: translate('Daily new post mail')},
                    ].map(({v, label}) => (
                        <FormControlLabel
                            key={v}
                            value={v}
                            control={<Radio size="small" sx={{p: 0.5}}/>}
                            label={
                                <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.primary}}>
                                    {label}
                                </Typography>
                            }
                        />
                    ))}
                </RadioGroup>
            </Box>

            <FormControlLabel
                control={
                    <Checkbox
                        size="small"
                        checked={newSponsor}
                        onChange={(e) => setNewSponsor(e.target.checked)}
                    />
                }
                label={
                    <Typography sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.primary}}>
                        {translate('New sponsor')}
                    </Typography>
                }
            />

            <Box>
                <Typography sx={{fontSize: `${t.type.caption.size}px`, fontWeight: 500, color: t.color.text.secondary, mb: 0.5}}>
                    {translate('Donation method')}
                </Typography>
                <Box
                    component="select"
                    value={donationMethod}
                    onChange={(e) => setDonationMethod(e.target.value)}
                    sx={{
                        width: '100%',
                        px: 1.25,
                        py: 0.75,
                        fontSize: `${t.type.body.size}px`,
                        borderRadius: `${t.radius.sm}px`,
                        border: `1px solid ${t.color.border.default}`,
                        backgroundColor: t.color.surface,
                        color: t.color.text.primary,
                    }}
                >
                    <option value="iban">IBAN</option>
                    <option value="stripe">Stripe</option>
                    <option value="paypal">PayPal</option>
                    <option value="other">{translate('Other')}</option>
                </Box>
            </Box>

            <LoadingButton
                onClick={sendTestMail}
                loading={sending}
                variant="contained"
                sx={{
                    textTransform: 'none',
                    fontSize: `${t.type.body.size}px`,
                    fontWeight: 500,
                    borderRadius: `${t.radius.sm}px`,
                    backgroundColor: t.color.accent.primary,
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: t.color.accent.primaryHover,
                        boxShadow: 'none',
                    },
                }}
            >
                {translate('Send test mail')}
            </LoadingButton>

            {result && (
                <Alert severity={result.type} sx={{fontSize: `${t.type.small.size}px`}}>
                    {result.text}
                </Alert>
            )}
        </Box>
    );
};

export default MailTestPanel;
